import React, {FC} from 'react';
import {useSelector} from 'react-redux';
import cn from 'classnames';
import {WeatherForecastHour} from '@/app/interfaces/weather';
import {LocationSunTime} from '@/common/interfaces/location';
import {temperatureUnitNames} from '@/common/constants/units';
import {selectUnitTypes} from "@/app/store/config/selectors";
import {useLocalizer} from '@/common/services/localizer';
import {useShouldHighlightCurrentHour} from '@/app/hooks/useShouldHighlightCurrentHour';
import {checkIsNightHour} from '@/app/helpers/checkIsNightHour';
import {hourReduce} from '@/common/helpers/unitReducers/hourReduce';
import {temperatureDisplayReduce} from '@/common/helpers/unitReducers/temperatureDisplayReduce';
import {pressureReduce} from '@/common/helpers/unitReducers/pressureReduce';
import {speedReduce} from '@/common/helpers/unitReducers/speedReduce';
import {ConditionSmallIcon} from '@/common/components/ConditionSmallIcon';
import {Tooltip} from '@/common/components/Tooltip';
import {TitledText} from '@/common/components/TitledText';
import {WindIcon} from '@/app/components/common/WindIcon';
import styles from './WeatherDailyHoursTable.css';

interface WeatherDailyHoursTableProps {
	hours: WeatherForecastHour[];
	currentHour?: number;
	dayIsPast: boolean;
	sunTime: LocationSunTime;
}

export const WeatherDailyHoursTable: FC<WeatherDailyHoursTableProps> = props => {
	const {currentHour, hours, dayIsPast, sunTime} = props;

	const highlightCurrentHour = useShouldHighlightCurrentHour(currentHour !== undefined);

	const hoursPerPeriod = hours.length / 4;

	const {temperatureUnit, pressureUnit, speedUnit, clockType} = useSelector(selectUnitTypes);

	const t = useLocalizer();

	const getHourAriaLabel = (hour: number) => {
		if (hour === 0) {
			return t('dayParts.midnight');
		}
		if (hour >= 1 && hour < 6) {
			return t('dayParts.night');
		}
		if (hour >= 6 && hour < 12) {
			return t('dayParts.morning');
		}
		if (hour === 12) {
			return t('dayParts.noon');
		}
		if (hour > 12 && hour < 18) {
			return t('dayParts.day');
		}
		if (hour >= 18 && hour < 22) {
			return t('dayParts.evening');
		}
		return t('dayParts.night');
	};

	// TODO bind rows to legend by aria "labeled by"
	return (
		<div className={styles.root}>
			<div className={styles.legend}>
				<p className={styles.legendRow}>
					{t('weatherParams.temperature')}, °{temperatureUnitNames[temperatureUnit]}
				</p>
				<Tooltip title={t('temperatureFeelHint')} positionX='right'>
					<p className={styles.legendRow}>
						<TitledText>{t('weatherParams.temperatureFeel')}</TitledText>
					</p>
				</Tooltip>
				<p className={styles.legendRow}>
					{t('weatherParams.pressure')}, {t(`unitAbbr.pressure.${pressureUnit}`)}
				</p>
				<p className={styles.legendRow}>{t('weatherParams.humidity')}, %</p>
				<p className={styles.legendRow}>
					{t('weatherParams.wind')}, {t(`unitAbbr.speed.${speedUnit}`)}
				</p>
				<p className={styles.legendRow}>{t('weatherParams.precipitation')}, %</p>
			</div>
			<table className={styles.table} cellSpacing='0' cellPadding='0'>
				<colgroup>
					{hours.map((item, index) => (
						<col
							key={item.hour}
							className={cn(
								styles.colgroup,
								!index || index % hoursPerPeriod || styles.isEdge,
								highlightCurrentHour && item.hour === currentHour && styles.isCurrent,
							)}
						/>
					))}
				</colgroup>
				<thead>
					<tr className={cn(styles.row, styles.isHeader)}>
						<td className={styles.cell} colSpan={hoursPerPeriod}>
							{t('dayParts.night')}
						</td>
						<td className={styles.cell} colSpan={hoursPerPeriod}>
							{t('dayParts.morning')}
						</td>
						<td className={styles.cell} colSpan={hoursPerPeriod}>
							{t('dayParts.day')}
						</td>
						<td className={styles.cell} colSpan={hoursPerPeriod}>
							{t('dayParts.evening')}
						</td>
					</tr>
					<tr className={cn(styles.row, styles.isSubheader)}>
						{hours.map(item => (
							<td
								aria-label={getHourAriaLabel(item.hour)}
								key={item.hour}
								className={cn(
									styles.cell,
									highlightCurrentHour && item.hour === currentHour && styles.isCurrentHour,
								)}>
								{hourReduce(item.hour, clockType)}
							</td>
						))}
					</tr>
				</thead>
				<tbody>
					<tr className={styles.row}>
						{hours.map(item => (
							<td key={item.hour} className={styles.cell}>
								<Tooltip
									className={styles.conditionIcon}
									title={t(`weatherConditions.${item.condition}`)}
									positionX='middle'>
									<ConditionSmallIcon
										condition={item.condition}
										isNight={checkIsNightHour(sunTime, item.hour)}
									/>
								</Tooltip>
							</td>
						))}
					</tr>
					<tr className={cn(styles.row, styles.isMainValue)}>
						{hours.map(item => (
							<td key={item.hour} className={styles.cell}>
								{temperatureDisplayReduce(item.temperature, temperatureUnit)}
							</td>
						))}
					</tr>
					<tr className={cn(styles.row, styles.isSecondaryValue)}>
						{hours.map(item => (
							<td key={item.hour} className={styles.cell}>
								{temperatureDisplayReduce(item.temperatureFeel, temperatureUnit)}
							</td>
						))}
					</tr>
					<tr className={cn(styles.row, styles.isHighlighted)}>
						{hours.map(item => (
							<td
								key={item.hour}
								className={cn(
									styles.cell,
									highlightCurrentHour && item.hour === currentHour && styles.isHighlightedCurrent,
								)}>
								{pressureReduce(item.pressure, pressureUnit)}
							</td>
						))}
					</tr>
					<tr className={styles.row}>
						{hours.map(item => (
							<td key={item.hour} className={styles.cell}>
								{item.humidity}
							</td>
						))}
					</tr>
					<tr className={cn(styles.row, styles.isHighlighted)}>
						{hours.map(item => (
							<td
								key={item.hour}
								className={cn(
									styles.cell,
									highlightCurrentHour && item.hour === currentHour && styles.isHighlightedCurrent,
								)}>
								<WindIcon
									className={styles.windDirection}
									speed={speedReduce(item.wind.speed, speedUnit)}
									speedUnit={speedUnit}
									direction={item.wind.direction}
								/>
								{speedReduce(item.wind.speed, speedUnit)}
							</td>
						))}
					</tr>
					<tr className={cn(styles.row, styles.isHigh)}>
						{hours.map(item => (
							<td key={item.hour} className={styles.cell}>
								{/* Show precipitation only for future days and future hours of the current day */}
								{item.precipitation &&
								!dayIsPast &&
								!(currentHour !== undefined && currentHour > item.hour)
									? item.precipitation
									: '-'}
							</td>
						))}
					</tr>
				</tbody>
			</table>
		</div>
	);
};

WeatherDailyHoursTable.displayName = 'WeatherDailyHoursTable';
